import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { isAdminSession } from '../admin/utils/adminPreview';
import {
  dismiss, getSummary, listNotifications, markAllRead, markRead, SEVERITY_COLOR, timeAgo,
  type Notification, type Summary,
} from '../../api/notificationsApi';

/**
 * The bell and the banner (SRS #101/#138), mounted once per shell.
 *
 * Polls the tiny /summary endpoint; opens a dropdown with the latest
 * notifications; shows the one warning/critical item as a banner across
 * the top of the content until it is read or dismissed.
 */
const POLL_MS = 60_000;

export const NotificationDock: React.FC<{ banner?: boolean }> = ({ banner = true }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<Summary>({ unread: 0, banner: null });
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const admin = isAdminSession();

  const refresh = useCallback(() => {
    if (admin) return;
    getSummary().then(setSummary).catch(() => {});
  }, [admin]);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { window.clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listNotifications({ limit: 12 }).then((r) => setItems(r.notifications)).catch(() => {}).finally(() => setLoading(false));
    const onDoc = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (admin) return null;

  const openItem = async (n: Notification) => {
    if (!n.read) { try { await markRead([n.id]); } catch { /* best-effort */ } }
    setOpen(false);
    refresh();
    if (n.link) navigate(n.link);
  };
  const dismissBanner = async (n: Notification) => {
    try { await dismiss(n.id); } catch { /* best-effort */ }
    setSummary((s) => ({ ...s, banner: null }));
  };

  const b = summary.banner;
  const c = b ? SEVERITY_COLOR[b.severity] : null;

  return (
    <>
      {banner && b && c && (
        // A fixed strip along the bottom of the viewport, independent of the host page's layout: inside a
        // row-flex <main> the banner used to become a full-height column beside the content (billing), and in
        // a column-flex one it stretched to fill the viewport (enterprise pages). Bottom, not top, so it never
        // covers the header's sign-out/bell; right padding clears the floating Support button.
        <div role="status" data-testid="notification-banner" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 890, display: 'flex', alignItems: 'center', gap: 12, padding: '8px 150px 8px 16px', minHeight: 40, maxHeight: 40, boxSizing: 'border-box', backgroundColor: c.bg, color: c.fg, borderTop: `1px solid ${c.border}`, fontSize: 13.5, backdropFilter: 'blur(6px)' }}>
          <strong style={{ whiteSpace: 'nowrap' }}>{b.title}</strong>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.body}</span>
          {b.link && <button type="button" onClick={() => openItem(b)} style={{ border: `1px solid ${c.border}`, background: 'var(--ds-surface)', color: c.fg, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: 600, fontSize: 12.5, whiteSpace: 'nowrap' }}>View</button>}
          <button type="button" aria-label="Dismiss" onClick={() => dismissBanner(b)} style={{ border: 'none', background: 'transparent', color: c.fg, cursor: 'pointer', display: 'flex' }}><X size={16} /></button>
        </div>
      )}
      <div ref={wrapRef} style={{ position: 'fixed', top: 12, right: 16, zIndex: 900 }}>
        <button type="button" aria-label={`Notifications${summary.unread ? ` (${summary.unread} unread)` : ''}`} onClick={() => setOpen((o) => !o)}
                style={{ position: 'relative', width: 38, height: 38, borderRadius: 999, border: '1px solid var(--ds-border, #e4e7ec)', background: 'var(--ds-surface, #fff)', color: 'var(--color-text, var(--ds-text-secondary))', cursor: 'pointer', display: 'grid', placeItems: 'center', boxShadow: '0 1px 3px rgba(16,24,40,0.1)' }}>
          <Bell size={18} aria-hidden />
          {summary.unread > 0 && (
            <span style={{ position: 'absolute', top: -4, right: -4, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: 'var(--ds-danger)', color: 'var(--ds-text-on-dark)', fontSize: 11, fontWeight: 700, display: 'grid', placeItems: 'center' }}>
              {summary.unread > 99 ? '99+' : summary.unread}
            </span>
          )}
        </button>
        {open && (
          <div style={{ position: 'absolute', right: 0, top: 44, width: 360, maxWidth: 'calc(100vw - 24px)', background: 'var(--ds-surface, #fff)', border: '1px solid var(--ds-border, #e4e7ec)', borderRadius: 12, boxShadow: '0 12px 32px rgba(16,24,40,0.16)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--ds-border, var(--ds-border))' }}>
              <strong style={{ fontSize: 14, color: 'var(--color-text, var(--ds-text))' }}>Notifications</strong>
              <div style={{ display: 'flex', gap: 10, fontSize: 12.5 }}>
                {summary.unread > 0 && <button type="button" onClick={async () => { await markAllRead().catch(() => {}); setItems((xs) => xs.map((x) => ({ ...x, read: true }))); refresh(); }} style={{ border: 'none', background: 'transparent', color: 'var(--ds-link)', cursor: 'pointer' }}>Mark all read</button>}
                <button type="button" onClick={() => { setOpen(false); navigate('/notifications'); }} style={{ border: 'none', background: 'transparent', color: 'var(--ds-link)', cursor: 'pointer' }}>See all</button>
              </div>
            </div>
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {loading && items.length === 0 && <p style={{ margin: 0, padding: 16, fontSize: 13, color: 'var(--ds-text-muted)' }}>Loading…</p>}
              {!loading && items.length === 0 && <p style={{ margin: 0, padding: 16, fontSize: 13, color: 'var(--ds-text-muted)' }}>Nothing yet. Renewals, replies, alerts and team activity show up here.</p>}
              {items.map((n) => {
                const col = SEVERITY_COLOR[n.severity];
                return (
                  <button key={n.id} type="button" onClick={() => openItem(n)}
                          style={{ display: 'grid', gridTemplateColumns: '8px 1fr auto', gap: 10, alignItems: 'start', width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', borderBottom: '1px solid var(--ds-border, var(--ds-surface-2))', background: n.read ? 'transparent' : 'rgba(68,76,231,0.05)', cursor: 'pointer' }}>
                    <span aria-hidden style={{ marginTop: 6, width: 8, height: 8, borderRadius: 999, background: n.read ? 'transparent' : col.fg }} />
                    <span>
                      <span style={{ display: 'block', fontSize: 13.5, fontWeight: n.read ? 500 : 700, color: 'var(--color-text, var(--ds-text))' }}>{n.title}</span>
                      {n.body && <span style={{ fontSize: 12.5, color: 'var(--ds-text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' } as React.CSSProperties}>{n.body}</span>}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--ds-text-faint)', whiteSpace: 'nowrap' }}>{timeAgo(n.created_at)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationDock;
